import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { getDownloadDesktopAppLink } from '@helpers/getDownloadDesktopAppLink';
import { getASDProtocolLink } from '@helpers/jobLinkHelpers';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery } from '@tanstack/react-query';
import { UserService } from 'app/API';
import { jobsKeys } from 'app/queries/jobs/jobsKeys';
import { useCallback, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { createFetchJobLockedStatusQueryFn } from './api/fetchJobLockedStatus';
import { ClickOpenBelow } from './styles/ClickOpenBelow';
import { CopyRight } from './styles/CopyRight';
import { DontHaveAutoScriptInstalled } from './styles/DontHaveAutoScriptInstalled';
import { DownloadNow } from './styles/DownloadNow';
import { Footer } from './styles/Footer';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { LaunchButtonWrapper } from './styles/LaunchButtonWrapper';
import { Root } from './styles/Root';
import { TermsPrivacy } from './styles/TermsPrivacy';
import { WaveWrapper } from './styles/WaveWrapper';
import Wave from './wave.svg';

export const ShareJobPage = () => {
  const { call } = useAPI();

  const { jobId } = useParams<{ jobId: string }>();
  const [searchParams] = useSearchParams();
  const version = searchParams.get('version') ?? undefined;

  const lockedStatusQuery = useQuery({
    queryKey: jobsKeys.lockedStatusByJobId(jobId ?? ''),
    queryFn: createFetchJobLockedStatusQueryFn(call),
    enabled: Boolean(jobId),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const downloadedClientMutation = useMutation({
    mutationFn: () => call(() => UserService.downloadedClient()),
  });

  const isLoading = !jobId || lockedStatusQuery.isLoading;
  const notFound = lockedStatusQuery.isError;
  const isLocked = lockedStatusQuery.data?.result ?? false;
  const shouldAutoLaunch = lockedStatusQuery.isSuccess && !lockedStatusQuery.isFetching && !isLocked;

  const launchHandler = useCallback(() => {
    if (!jobId) {
      return;
    }
    window.open(new URL(getASDProtocolLink(jobId, version)), '_self');
  }, [jobId, version]);

  useEffect(() => {
    if (!shouldAutoLaunch) {
      return;
    }
    const launchTimeout = setTimeout(launchHandler, 1000);

    return () => clearTimeout(launchTimeout);
  }, [shouldAutoLaunch, launchHandler]);

  if (isLoading) {
    return (
      <Root>
        <AbsoluteSpinner />
      </Root>
    );
  }

  if (notFound) {
    return (
      <Root>
        <ClickOpenBelow>
          <p>
            Sorry, this job was <b>not found</b>.
          </p>
        </ClickOpenBelow>

        <Footer>
          <TermsPrivacy>
            <a href="https://voicescript.ai/privacy-policy/" target="_blank" rel="noreferrer">
              Privacy Policy
            </a>{' '}
            |{' '}
            <a href="https://voicescript.ai/terms-of-service/" target="_blank" rel="noreferrer">
              Terms of Service
            </a>
          </TermsPrivacy>
          <CopyRight>&copy; {new Date().getFullYear()} VoiceScript Inc. All Rights Reserved</CopyRight>
        </Footer>
      </Root>
    );
  }

  if (isLocked) {
    return (
      <Root>
        <ClickOpenBelow>
          <p>
            This job is <b>locked</b>. In order to unlock this job you need to <b>pay the remaining sum</b>
          </p>
        </ClickOpenBelow>

        <Footer>
          <TermsPrivacy>
            <a href="https://voicescript.ai/privacy-policy/" target="_blank" rel="noreferrer">
              Privacy Policy
            </a>{' '}
            |{' '}
            <a href="https://voicescript.ai/terms-of-service/" target="_blank" rel="noreferrer">
              Terms of Service
            </a>
          </TermsPrivacy>
          <CopyRight>&copy; {new Date().getFullYear()} VoiceScript Inc. All Rights Reserved</CopyRight>
        </Footer>
      </Root>
    );
  }

  return (
    <Root>
      <ClickOpenBelow>
        <p>
          Click <b>Open AutoScript Desktop App</b> on the dialog shown by your browser.
        </p>
        <p>
          If you don’t see a dialog, click <b>Launch App</b> below.
        </p>
      </ClickOpenBelow>
      <LaunchButtonWrapper>
        <Button className="mx-auto w-[190px]" onClick={launchHandler}>
          Launch AutoScript App
        </Button>
      </LaunchButtonWrapper>
      <WaveWrapper>
        <Wave />
      </WaveWrapper>
      <DontHaveAutoScriptInstalled>Don’t have AutoScript Desktop App installed?</DontHaveAutoScriptInstalled>
      <DownloadNow href={getDownloadDesktopAppLink()} target="_blank" onClick={() => downloadedClientMutation.mutate()}>
        Download Now
      </DownloadNow>

      <Footer>
        <TermsPrivacy>
          <a href="https://voicescript.ai/privacy-policy/" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>{' '}
          |{' '}
          <a href="https://voicescript.ai/terms-of-service/" target="_blank" rel="noreferrer">
            Terms of Service
          </a>
        </TermsPrivacy>
        <CopyRight>&copy; {new Date().getFullYear()} VoiceScript Inc. All Rights Reserved</CopyRight>
      </Footer>
    </Root>
  );
};
